import { useState } from 'react';
import api from '../api'; // Adjust the import path as necessary
import NameModal from './NameModal';
import { getUserName, saveUserNameFromModal, markNameModalSkipped, hasSkippedNameModal } from '../utils/userName';

const InviteCode = ({ setAuth, logo }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [pendingName, setPendingName] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/auth', { code });
      localStorage.setItem('weddingToken', data.token);
      
      // Check if we should show the name modal
      const existingName = getUserName();
      const hasSkipped = hasSkippedNameModal();
      
      if (!existingName && !hasSkipped) {
        // Show name modal before setting auth
        setShowNameModal(true);
        setLoading(false);
      } else {
        // Proceed directly to dashboard
        setAuth(true);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error authenticating');
      setLoading(false);
    }
  };

  const handleNameSave = () => {
    const savedName = saveUserNameFromModal(pendingName);
    if (savedName) {
      setShowNameModal(false);
      setAuth(true);
    }
  };

  const handleNameCancel = () => {
    markNameModalSkipped();
    setShowNameModal(false);
    setAuth(true);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-background p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={logo} alt="Wedding Logo" className="w-48 h-48 mx-auto mb-6 object-contain" />
          <h1 className="text-3xl font-serif text-secondary mb-2">Welcome</h1>
          <p className="text-secondary-70 text-sm leading-relaxed">
            Enter your invite code to access the wedding gallery
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 bg-surface/80 backdrop-blur-sm border border-accent-20 rounded-2xl p-6 shadow-lg">
          <div>
            <label htmlFor="invite-code" className="block text-sm font-medium text-secondary mb-2">
              Invite Code
            </label>
            <input
              id="invite-code"
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="Enter your invite code"
              className="w-full p-4 bg-neutral-5 border-2 border-neutral-20 rounded-xl text-center text-lg font-mono tracking-wider focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary-20 focus:bg-surface transition-all duration-200"
              maxLength={16}
              autoComplete="off"
              autoFocus
            />
          </div>

          <button
            type="submit"
            disabled={loading || code.length < 4}
            className="w-full p-4 bg-primary text-surface rounded-xl font-semibold shadow-lg hover:bg-primary-90 hover:shadow-xl disabled:bg-neutral-30 disabled:text-neutral-60 disabled:shadow-none disabled:cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            {loading ? (
              <div className="flex items-center justify-center space-x-2">
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                </svg>
                <span>Verifying...</span>
              </div>
            ) : (
              'Access Gallery'
            )}
          </button>

          {error && (
            <div className="p-4 bg-accent-10 border border-accent-30 rounded-xl">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-secondary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" />
                </svg>
                <p className="text-secondary text-sm">{error}</p>
              </div>
            </div>
          )}
        </form>

        <div className="mt-8 text-center">
          <p className="text-xs text-secondary-70 px-3 py-2 rounded-lg inline-block">
            Having trouble? Check your invitation for the correct code
          </p>
        </div>
      </div>

      {/* Name Modal */}
      <NameModal
        isOpen={showNameModal}
        pendingName={pendingName}
        onChangePendingName={setPendingName}
        onCancel={handleNameCancel}
        onSave={handleNameSave}
      />
    </div>
  );
};

export default InviteCode;